/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useRef } from 'react';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { cssMap, cx, jsx } from '@atlaskit/css';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import useAutoFocus from '@atlaskit/ds-lib/use-auto-focus';
import { useId } from '@atlaskit/ds-lib/use-id';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { SplitButtonContext } from '../../containers/split-button/split-button-context';
import { useSplitButtonContext } from '../../containers/split-button/use-split-button-context';
import {
	type Appearance,
	type CommonBaseProps,
	type CommonButtonProps,
	type Spacing,
} from '../types';
import blockEvents from './block-events';
import renderLoadingOverlay from './loading-overlay';

const LOADING_LABEL = ', Loading';

export type ControlledEvents<TagName extends HTMLElement> = Pick<
	React.DOMAttributes<TagName>,
	| 'onMouseDownCapture'
	| 'onMouseUpCapture'
	| 'onKeyDownCapture'
	| 'onKeyUpCapture'
	| 'onTouchStartCapture'
	| 'onTouchEndCapture'
	| 'onPointerDownCapture'
	| 'onPointerUpCapture'
	| 'onClickCapture'
	| 'onMouseOver'
	| 'onMouseOut'
	| 'onMouseMove'
	| 'onFocus'
	| 'onBlur'
	| 'onMouseDown'
	| 'onMouseUp'
	| 'onKeyDown'
	| 'onKeyUp'
	| 'onTouchStart'
	| 'onTouchEnd'
	| 'onPointerDown'
	| 'onPointerUp'
> &
	Pick<CommonButtonProps<TagName>, 'onClick'>;

type ButtonBaseProps<TagName extends HTMLElement> = CommonBaseProps &
	Pick<
		CommonButtonProps<TagName>,
		'autoFocus' | 'isDisabled' | 'isSelected' | 'testId' | 'analyticsContext' | 'interactionName'
	> & {
		appearance?: Appearance;
		isLoading?: boolean;
		isIconButton?: boolean;
		isCircle?: boolean;
		hasIconBefore?: boolean;
		hasIconAfter?: boolean;
		shouldFitContainer?: boolean;
		spacing?: Spacing;
		// eslint-disable-next-line @repo/internal/react/consistent-props-definitions
		ariaLabel?: string;
		// eslint-disable-next-line @repo/internal/react/consistent-props-definitions
		ariaLabelledBy?: string;
		children: React.ReactNode;
		ref: React.Ref<TagName>;
		type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
		componentName?: string;
		role?: string;
		iconButtonRef?: React.Ref<HTMLButtonElement>;
		tooltipRef?: React.Ref<HTMLButtonElement>;
		onMouseOver?: React.MouseEventHandler<HTMLButtonElement> | React.MouseEventHandler<TagName>;
		onMouseOutonMouseOver?: React.MouseEventHandler<HTMLButtonElement>;
		onMouseMoveonMouseOver?: React.MouseEventHandler<HTMLButtonElement>;
		onMouseDownonMouseOver?: React.MouseEventHandler<HTMLButtonElement>;
		onMouseMove?: React.MouseEventHandler<HTMLButtonElement>;
		onFocus?: React.FocusEventHandler<HTMLButtonElement>;
		onBlur?: React.FocusEventHandler<HTMLButtonElement>;
		onClick?:
			| ((e: React.MouseEvent<TagName>, analyticsEvent: UIAnalyticsEvent) => void)
			| undefined;
	} & ControlledEvents<TagName>;

const styles = cssMap({
	base: {
		display: 'inline-flex',
		boxSizing: 'border-box',
		width: 'auto',
		maxWidth: '100%',
		position: 'relative',
		alignItems: 'baseline',
		justifyContent: 'center',
		columnGap: token('space.050'),
		borderRadius: token('radius.medium'),
		borderWidth: 0,
		flexShrink: 0,
		height: '2rem',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		paddingBlock: token('space.075'),
		paddingInlineEnd: token('space.150'),
		paddingInlineStart: token('space.150'),
		textAlign: 'center',
		transition: 'background 0.1s ease-out',
		verticalAlign: 'middle',
		'&::after': {
			borderRadius: 'inherit',
			inset: token('space.0'),
			borderStyle: 'solid',
			borderWidth: token('border.width'),
			pointerEvents: 'none',
			position: 'absolute',
		},
	},
	interactiveMotion: {
		transition: token('motion.button.hovered'),
		'&:active': {
			transition: token('motion.button.pressed'),
		},
	},
	// Required due to Jira's AUI CSS reset: https://product-fabric.atlassian.net/browse/DSP-15687
	linkDecorationUnset: {
		textDecoration: 'none',
		'&:hover': { textDecoration: 'none' },
		'&:active': { textDecoration: 'none' },
		'&:focus': { textDecoration: 'none' },
	},
	disabled: {
		cursor: 'not-allowed',
		color: token('color.text.disabled'),
		'&:hover': {
			color: token('color.text.disabled'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.disabled'),
		},
	},
	// Shared diabled styles for primary, warning, danger, and discovery appearances
	sharedDisabled: {
		backgroundColor: token('color.background.disabled'),
		'&:hover': {
			// @ts-expect-error
			backgroundColor: token('color.background.disabled'),
		},
		'&:active': {
			// @ts-expect-error
			backgroundColor: token('color.background.disabled'),
		},
	},
	spacingCompact: {
		columnGap: token('space.050'),
		height: '1.5rem',
		paddingBlock: token('space.025'),
		paddingInlineEnd: token('space.150'),
		paddingInlineStart: token('space.150'),
		verticalAlign: 'middle',
		borderRadius: token('radius.small'),
	},
	circle: {
		borderRadius: token('radius.full'),
	},
	fullWidth: {
		width: '100%',
	},
	loading: {
		cursor: 'progress',
	},
	iconButton: {
		height: '2rem',
		width: '2rem',
		paddingInlineEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
	},
	iconButtonCompact: {
		height: '1.5rem',
		width: '1.5rem',
	},
	buttonIconBefore: {
		paddingInlineStart: token('space.150'),
		columnGap: token('space.075'),
	},
	buttonIconAfter: {
		paddingInlineEnd: token('space.150'),
		columnGap: token('space.075'),
	},
	splitButton: {
		'&:focus-visible': {
			zIndex: 100,
		},
	},
	loadingOverlay: {
		display: 'flex',
		position: 'absolute',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		insetBlockEnd: token('space.0'),
		insetBlockStart: token('space.0'),
		insetInlineEnd: token('space.0'),
		insetInlineStart: token('space.0'),
	},
	navigationSplitButton: {
		width: '24px',
		backgroundColor: token('color.background.neutral.subtle'),
		paddingInlineEnd: token('space.075'),
		paddingInlineStart: token('space.075'),
	},
});

const defaultStyles = cssMap({
	root: {
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		'&::after': {
			content: '""',
			borderColor: token('color.border'),
		},
		'&:visited': {
			color: token('color.text.subtle'),
		},
		'&:hover': {
			color: token('color.text.subtle'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.subtle'),
		},
		'&:focus': {
			color: token('color.text.subtle'),
		},
	},
	interactive: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: token('color.text.subtle'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			// @ts-expect-error
			color: token('color.text.subtle'),
		},
	},
	disabled: {
		backgroundColor: 'transparent',
		'&:hover': {
			// @ts-expect-error
			backgroundColor: 'transparent',
		},
		'&:active': {
			// @ts-expect-error
			backgroundColor: 'transparent',
		},
		'&::after': {
			borderColor: token('color.border.disabled'),
		},
	},
});

const primaryStyles = cssMap({
	root: {
		backgroundColor: token('color.background.brand.bold'),
		color: token('color.text.inverse'),
		'&:visited': {
			color: token('color.text.inverse'),
		},
		'&:hover': {
			color: token('color.text.inverse'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.inverse'),
		},
		'&:focus': {
			color: token('color.text.inverse'),
		},
	},
	interactive: {
		'&:hover': {
			color: token('color.text.inverse'),
			backgroundColor: token('color.background.brand.bold.hovered'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.inverse'),
			backgroundColor: token('color.background.brand.bold.pressed'),
		},
	},
});

const rovoStyles = cssMap({
	root: {
		backgroundColor: token('color.rovo.background.brand.bold'),
		color: token('color.text.inverse'),
		'&:visited': {
			color: token('color.text.inverse'),
		},
		'&:hover': {
			color: token('color.text.inverse'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.inverse'),
		},
		'&:focus': {
			color: token('color.text.inverse'),
		},
	},
	interactive: {
		'&:hover': {
			color: token('color.text.inverse'),
			backgroundColor: token('color.rovo.background.brand.bold.hovered'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.inverse'),
			backgroundColor: token('color.rovo.background.brand.bold.pressed'),
		},
	},
});

const warningStyles = cssMap({
	root: {
		backgroundColor: token('color.background.warning.bold'),
		color: token('color.text.warning.inverse'),
		'&:visited': {
			color: token('color.text.warning.inverse'),
		},
		'&:hover': {
			color: token('color.text.warning.inverse'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.warning.inverse'),
		},
		'&:focus': {
			color: token('color.text.warning.inverse'),
		},
	},
	interactive: {
		'&:hover': {
			color: token('color.text.warning.inverse'),
			backgroundColor: token('color.background.warning.bold.hovered'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.warning.inverse'),
			backgroundColor: token('color.background.warning.bold.pressed'),
		},
	},
});

const dangerStyles = cssMap({
	root: {
		backgroundColor: token('color.background.danger.bold'),
		color: token('color.text.inverse'),
		'&:visited': {
			color: token('color.text.inverse'),
		},
		'&:hover': {
			color: token('color.text.inverse'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.inverse'),
		},
		'&:focus': {
			color: token('color.text.inverse'),
		},
	},
	interactive: {
		'&:hover': {
			color: token('color.text.inverse'),
			backgroundColor: token('color.background.danger.bold.hovered'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.inverse'),
			backgroundColor: token('color.background.danger.bold.pressed'),
		},
	},
});

const discoveryStyles = cssMap({
	root: {
		backgroundColor: token('color.background.discovery.bold'),
		color: token('color.text.inverse'),
		'&:visited': {
			color: token('color.text.inverse'),
		},
		'&:hover': {
			color: token('color.text.inverse'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.inverse'),
		},
		'&:focus': {
			color: token('color.text.inverse'),
		},
	},
	interactive: {
		'&:hover': {
			color: token('color.text.inverse'),
			backgroundColor: token('color.background.discovery.bold.hovered'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.inverse'),
			backgroundColor: token('color.background.discovery.bold.pressed'),
		},
	},
});

const subtleStyles = cssMap({
	root: {
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		'&:visited': {
			color: token('color.text.subtle'),
		},
		'&:hover': {
			color: token('color.text.subtle'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.subtle'),
		},
		'&:focus': {
			color: token('color.text.subtle'),
		},
	},
	interactive: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: token('color.text.subtle'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			// @ts-expect-error
			color: token('color.text.subtle'),
		},
	},
	disabled: {
		backgroundColor: 'transparent',
		'&:hover': {
			// @ts-expect-error
			backgroundColor: 'transparent',
		},
		'&:active': {
			// @ts-expect-error
			backgroundColor: 'transparent',
		},
		'&::after': {
			borderColor: token('color.border.disabled'),
		},
	},
});

const selectedStyles = cssMap({
	root: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&::after': {
			content: '""',
			borderColor: token('color.border.selected'),
		},
		'&:visited': {
			color: token('color.text.selected'),
		},
		'&:hover': {
			color: token('color.text.selected'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.selected'),
		},
		'&:focus': {
			color: token('color.text.selected'),
		},
	},
	insideSplitButton: {
		// This is 2 so it appears above the split button divider when selected.
		// See split-button.tsx.
		// We need a local zindex just for button.
		// @ts-expect-error
		zIndex: 2,
	},
	interactive: {
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
			color: token('color.text.selected'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
			// @ts-expect-error
			color: token('color.text.selected'),
		},
	},
	// TODO: Remove me once we kill color fallbacks
	warning: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			color: token('color.text.selected'),
			// @ts-expect-error
			backgroundColor: token('color.background.selected'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.selected'),
			// @ts-expect-error
			backgroundColor: token('color.background.selected'),
		},
	},
	// TODO: Remove me once we kill color fallbacks
	danger: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			color: token('color.text.selected'),
			// @ts-expect-error
			backgroundColor: token('color.background.selected'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.selected'),
			// @ts-expect-error
			backgroundColor: token('color.background.selected'),
		},
	},
	// TODO: Remove me once we kill color fallbacks
	discovery: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			color: token('color.text.selected'),
			// @ts-expect-error
			backgroundColor: token('color.background.selected'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.selected'),
			// @ts-expect-error
			backgroundColor: token('color.background.selected'),
		},
	},
});

const brandSelectedSplitButtonStyles = cssMap({
	root: {
		backgroundColor: token('color.background.brand.subtlest'),
		color: token('color.text.brand'),
		'&::after': {
			content: '""',
			borderColor: token('color.border.brand'),
		},
		'&:visited': {
			color: token('color.text.brand'),
		},
		'&:hover': {
			color: token('color.text.brand'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text.brand'),
		},
		'&:focus': {
			color: token('color.text.brand'),
		},
	},
	interactive: {
		'&:hover': {
			backgroundColor: token('color.background.brand.subtlest.hovered'),
			color: token('color.text.brand'),
		},
		'&:active': {
			backgroundColor: token('color.background.brand.subtlest.pressed'),
			// @ts-expect-error
			color: token('color.text.brand'),
		},
	},
});

/**
 * __button base__
 *
 * - Implements auto focus when enabled.
 * - Appends the `onClick` event with UFO analytics tracking.
 *
 * @private
 */
const ButtonBase: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<ButtonBaseProps<HTMLButtonElement>> & React.RefAttributes<HTMLButtonElement>
> = React.forwardRef(
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore: to unblock React 18.2.0 -> 18.3.1 version bump in Jira
	(
		{
			appearance: propAppearance,
			autoFocus = false,
			isDisabled: propIsDisabled = false,
			isLoading = false,
			isSelected: propIsSelected = false,
			isIconButton = false,
			isCircle = false,
			hasIconBefore = false,
			hasIconAfter = false,
			shouldFitContainer = false,
			spacing: propSpacing = 'default',
			ariaLabel,
			ariaLabelledBy,
			children,
			interactionName,
			onClick,
			onMouseDown,
			onMouseDownCapture,
			onMouseUp,
			onMouseUpCapture,
			onKeyDown,
			onKeyDownCapture,
			onKeyUp,
			onKeyUpCapture,
			onTouchStart,
			onTouchStartCapture,
			onTouchEnd,
			onTouchEndCapture,
			onPointerDown,
			onPointerDownCapture,
			onPointerUp,
			onPointerUpCapture,
			onClickCapture,
			testId,
			analyticsContext,
			componentName,
			role,
			onMouseOver,
			onMouseOut,
			onFocus,
			onBlur,
			onMouseMove,
			type,
			...unsafeRest
		}: ButtonBaseProps<HTMLButtonElement>,
		ref: React.Ref<HTMLButtonElement>,
	): JSX.Element => {
		// React Compiler opt-out: RC-incompatible (memoization breaks runtime behaviour).
		'use no memo';

		const localRef = useRef<HTMLButtonElement | null>(null);
		const splitButtonContext = useSplitButtonContext();
		const loadingLabelId = useId();
		const isSplitButton = Boolean(splitButtonContext);
		const isNavigationSplitButton = splitButtonContext?.isNavigationSplitButton || false;
		const isDefaultAppearanceSplitButton = splitButtonContext?.appearance === 'default';
		const appearance = isDefaultAppearanceSplitButton
			? 'subtle'
			: propAppearance || splitButtonContext?.appearance || 'default';

		const spacing = splitButtonContext?.spacing || propSpacing;
		const isDisabled = splitButtonContext?.isDisabled || propIsDisabled;
		const isInteractive = !isDisabled && !isLoading;
		// Also treat loading buttons as disabled
		const isEffectivelyDisabled = isDisabled || isLoading;
		const isSelected = propIsSelected && !isDisabled;
		// The open dropdown is part of a primary action, not a standalone selection.
		const isBrandSelectedSplitButton =
			isSelected &&
			appearance === 'primary' &&
			splitButtonContext?.appearance === 'primary' &&
			fg('platform-dst-tokens-finesse');

		useAutoFocus(localRef, autoFocus);

		// @ts-expect-error
		const { className: _className, css: _css, as: _as, style: _style, ...saferRest } = unsafeRest;

		return (
			<Pressable
				{...saferRest}
				componentName={componentName || 'button'}
				analyticsContext={analyticsContext}
				role={role}
				ref={mergeRefs([localRef, ref])}
				xcss={cx(
					styles.base,
					isInteractive && styles.interactiveMotion,
					appearance === 'default' && defaultStyles.root,
					appearance === 'default' && isInteractive && defaultStyles.interactive,
					appearance === 'primary' && primaryStyles.root,
					appearance === 'primary' && isInteractive && primaryStyles.interactive,
					appearance === 'rovo' && rovoStyles.root,
					appearance === 'rovo' && isInteractive && rovoStyles.interactive,
					appearance === 'warning' && warningStyles.root,
					appearance === 'warning' && isInteractive && warningStyles.interactive,
					appearance === 'danger' && dangerStyles.root,
					appearance === 'danger' && isInteractive && dangerStyles.interactive,
					appearance === 'discovery' && discoveryStyles.root,
					appearance === 'discovery' && isInteractive && discoveryStyles.interactive,
					appearance === 'subtle' && subtleStyles.root,
					appearance === 'subtle' && isInteractive && subtleStyles.interactive,
					styles.linkDecorationUnset,
					isSelected && selectedStyles.root,
					isSelected && isSplitButton && selectedStyles.insideSplitButton,
					isSelected && isInteractive && selectedStyles.interactive,
					isBrandSelectedSplitButton && brandSelectedSplitButtonStyles.root,
					isBrandSelectedSplitButton && isInteractive && brandSelectedSplitButtonStyles.interactive,
					// TODO: remove me once we kill color fallbacks
					isSelected && appearance === 'danger' && selectedStyles.danger,
					// TODO: remove me once we kill color fallbacks
					isSelected && appearance === 'warning' && selectedStyles.warning,
					// TODO: remove me once we kill color fallbacks
					isSelected && appearance === 'discovery' && selectedStyles.discovery,
					isDisabled && styles.disabled,
					isDisabled &&
						appearance !== 'default' &&
						appearance !== 'subtle' &&
						styles.sharedDisabled,
					isDisabled && appearance === 'default' && defaultStyles.disabled,
					spacing === 'compact' && styles.spacingCompact,
					// This must come after spacing compact styling as it overrides the border radius
					isCircle && !isSplitButton && styles.circle,
					shouldFitContainer && styles.fullWidth,
					hasIconBefore && styles.buttonIconBefore,
					hasIconAfter && styles.buttonIconAfter,
					isIconButton && styles.iconButton,
					isIconButton && spacing === 'compact' && styles.iconButtonCompact,
					isLoading && styles.loading,
					isSplitButton && styles.splitButton,
					isNavigationSplitButton && styles.navigationSplitButton,
				)}
				isDisabled={isDisabled}
				aria-live="polite"
				{...(isLoading && { 'aria-disabled': true })}
				aria-label={
					isLoading && ariaLabel && !ariaLabelledBy ? `${ariaLabel} ${LOADING_LABEL}` : ariaLabel
				}
				aria-labelledby={
					isLoading && ariaLabelledBy ? `${ariaLabelledBy} ${loadingLabelId}` : ariaLabelledBy
				}
				onClick={onClick}
				{...blockEvents(isEffectivelyDisabled, {
					onMouseDownCapture,
					onMouseUpCapture,
					onKeyDownCapture,
					onKeyUpCapture,
					onTouchStartCapture,
					onTouchEndCapture,
					onPointerDownCapture,
					onPointerUpCapture,
					onClickCapture,
				})}
				testId={testId}
				onMouseOver={onMouseOver}
				onFocus={onFocus}
				onMouseMove={onMouseMove}
				onBlur={onBlur}
				type={type}
				interactionName={interactionName}
				onMouseDown={onMouseDown}
				onMouseUp={onMouseUp}
				onKeyDown={onKeyDown}
				onMouseOut={onMouseOut}
				onKeyUp={onKeyUp}
				onTouchStart={onTouchStart}
				onTouchEnd={onTouchEnd}
				onPointerDown={onPointerDown}
				onPointerUp={onPointerUp}
			>
				<SplitButtonContext.Provider value={undefined}>
					{children}
					{isLoading && (
						<span css={styles.loadingOverlay}>
							{renderLoadingOverlay({
								spacing,
								appearance,
								isDisabled,
								isSelected,
								testId,
							})}
						</span>
					)}
					{isLoading && (ariaLabelledBy || !ariaLabel) && (
						<VisuallyHidden id={loadingLabelId}>{LOADING_LABEL}</VisuallyHidden>
					)}
				</SplitButtonContext.Provider>
			</Pressable>
		);
	},
);

export default ButtonBase;
